
import { DocumentNode, useLazyQuery, useMutation, useQuery } from '@apollo/client';
import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Flex, Box, Text, Image, Button } from 'rebass/styled-components'
import { toast } from 'react-toastify';
import parse, { Element as ParserElement } from 'html-react-parser';
import { GET_CONVERSATIONS, GET_CONVERSATION_BY_ID, GET_PATIENT_BY_ID, GET_PATIENT_CASES, GET_PATIENT_CONVERSATIONS_LIST } from '../../../graphql/queries';
import { ADD_NOTE_TO_CONVERSATION, REPLY_TO_CONVERSATION, TOUCH, UNSNOOZE_CONVERSATION } from '../../../graphql/mutations';
import { IGetConversationByIdOutput, IHealthCoachGetPatientConversationsListInput, IHealthCoachGetPatientConversationsListOutput, IIGetConversationByIdInput, IThread, IThreadOutput } from '../../../graphql/types/queries/conversation';
import { IUser } from '../../../graphql/types/queries/user';
import withSession from '../../../lib/withSession';
import styled from 'styled-components';
import { MessageAvatarNoPicStyled, MessageAvatarStyled, MessageTextStyled } from '../../../components/conversation/styled/bubbles';
import Modal from 'styled-react-modal';
import moment from 'moment';
import TextEditor from './TextEditor';
import { ConversationToolbar } from './ConversationToolbar';
import { replaceAll } from '../../../utils/StringUtils';
import { IAddNoteToConversationInput, IAddNoteToConversationOutput, IReplyToConversationInput, IReplyToConversationOutput, IUnsnoozeConversationInput, IUnsnoozeConversationOutput } from '../../../graphql/types/mutations/conversation';
import { AttachmentFileFromHS } from './AttachmentFile';
import { ConversationStatusCard } from './ConversationStatusCard';
import PleaseWait from '../../../components/PleaseWait';
import { Tag } from '../../../components/Tag';
import { LoadingMessages } from '../../../components/icons/LoadingMessages';
import { Constants } from '../../../utils/Constants';
import { getMockedFirstName, getMockedLastName } from '../../../utils/mocks';
import { Link, useNavigate } from 'react-router-dom';
import { PatientEmbed } from '../../Patient';
import { SpecialModalBackground } from '../../../components/Commons';
import { ConvoTagging } from './ConvoTagging';
import { IGetProviderPatientByIdInput, IGetProviderPatientByIdOutput } from '../../../graphql/types/queries/patient';
import { ViewListConversations } from './ViewListConversations';


interface ICommunicatorMessagerProps {
  conversationId: string,
  session: IUser
  onSelectConversation?: (c: string) => void
  athenaId?: string | undefined
}

const getLineBackground = (type: 'beaconchat' | 'chat' | 'customer' | 'forwardchild' | 'forwardparent' | 'lineitem' | 'message' | 'note' | 'phone') => {
  switch (type) {
    case 'note':
      return '#fab347'
    case 'message':
      return '#fab347'

    default:
      break;
  }
}

const ThreadLine = styled(Box) <{ type: 'beaconchat' | 'chat' | 'customer' | 'forwardchild' | 'forwardparent' | 'lineitem' | 'message' | 'note' | 'phone', isSelf: boolean, isDraft?: boolean }>`
  height: calc(100% + 50px);
  margin-top: -25px;
  width: 5px;
  background: ${props => props.type === 'note' ? '#ffc555' : props.isDraft ? '#1292ee' : props.isSelf ? '#344F7A' : 'transparent'};
`

export const getDateText = (thread: IThread) => {
  const date = moment(thread.createdAt)
  const diffHours = moment().diff(date, 'hours')
  if (diffHours < 1) {
    const diffMinutes = moment().diff(date, 'minutes')
    return `${diffMinutes} min${diffMinutes > 1 ? 's' : ''} ago`
  }

  if (diffHours < 24) {
    return `${diffHours} hr${diffHours > 1 ? 's' : ''} ago`
  }

  if (moment().isSame(date, 'year')) {
    return date.format('MMM DD, h:mma')
  } else {
    return date.format('MMM DD YYYY, h:mma')
  }
}
const LineItem = ({ thread }: { thread: IThread }) => {
  return (
    <Flex width={'100%'} backgroundColor='#f9fafa' padding={'12px 25px 12px 76px'} style={{ borderBottom: '1px solid #e5e9ec' }}>
      <MessageTextStyled fontSize={'14px'} flex={1}
        dangerouslySetInnerHTML={{ __html: replaceAll((thread.action?.text || ''), 'You ', 'G from Paloma Health ').replace(' **', '&nbsp;<strong>').replace('** ', '</strong>&nbsp;') }}
      />
      <Text color='#a5b2bd' fontSize={'12px'} >{getDateText(thread)}</Text>
    </Flex>
  )
}

const Thread = ({ thread, session, onEditDraft }: { thread: IThread, session: IUser, onEditDraft: (thread: IThread) => void }) => {
  const [modalContent, setModalContent] = useState<any>(null);
  const parser = (input: string) => {
    return parse(input, {
      replace: domNode => {
        if ((domNode as ParserElement).name === 'img') {
          return <Image style={{ cursor: 'pointer' }} maxWidth={'200px'} src={(domNode as ParserElement).attribs.src} onClick={() => {
            setModalContent(<Image style={{ objectFit: 'contain' }} src={(domNode as ParserElement).attribs.src} />)
          }} />
        }
      }
    })
  }
  const isSelf = !!session.email && ((thread.createdBy || {}).email || '').toLowerCase() === ((session.email || '').toLowerCase() === 'g+5@palomahealth.com' ? 'ac.herbalmd@gmail.com' : (session.email || '').toLowerCase())
  return (
    <Flex style={{ width: '100%', borderBottom: '1px solid #e5e9ec' }} paddingY={'25px'} paddingRight={'25px'}>
      <ThreadLine type={thread.type} isSelf={isSelf} mr={3} isDraft={(thread.state || '') === 'draft'} />
      {!!thread.createdBy.photoUrl && <MessageAvatarStyled photoUrl={thread.createdBy.photoUrl} />}
      {!thread.createdBy.photoUrl && (
        <MessageAvatarNoPicStyled>
          <Text color='white'>{thread.createdBy.first[0].toUpperCase()}{thread.createdBy.last[0].toUpperCase()}</Text>
        </MessageAvatarNoPicStyled>
      )}
      <Flex flexDirection={'column'} flex={1} ml={2}>
        <Flex style={{ minHeight: '40px!important' }} paddingBottom={'6px'}>
          <Flex flex={1} flexDirection={['column', 'row', 'row']} alignItems={'center'}>
            <Text fontWeight={500} fontSize='18px'>{!thread.action || !thread.action.type || thread.action.type.indexOf('workflow') < 0 ? Constants.IS_SCRAMBLING_MODE ? `${getMockedFirstName()} ${getMockedLastName()}` : `${thread.createdBy.first} ${thread.createdBy.last}` : `Worflows`}</Text>
            {thread.state !== 'draft' ? (
              <Text ml={[0, 1, 1]} fontWeight={500} fontSize='18px' color={thread.type === 'note' ? '#fab347' : '#8494a5'}>
                {thread.type === 'customer' || thread.type === 'message' ? ' replied' : thread.type === 'note' ? ' added a note' : ''}
              </Text>
            ) : (
              <Flex>
                <Text ml={[0, 1, 1]} mr={3} fontWeight={500} fontSize='18px' color={'#07c'}>
                  created a draft
                </Text>
                {/* <Button onClick={() => onEditDraft(thread)} type={'button'} style={{ padding: '0 4px', borderRadius: '4px 0 0 4px', background: 'white', color: 'rgb(116, 132, 148)', border: '1px solid rgb(116, 132, 148)', borderRight: '0px', fontSize: '12px' }}>
                  Edit
                </Button>
                <Button type={'button'} style={{ padding: '0 4px', borderRadius: '0 4px 4px 0', background: 'white', color: 'rgb(116, 132, 148)', border: '1px solid rgb(116, 132, 148)', fontSize: '12px' }}>
                  Discard
                </Button> */}
              </Flex>
            )}
          </Flex>
          <Flex flexDirection={'column'} ml={3} alignItems='flex-end'>
            <Text color='#a5b2bd' fontSize={'12px'} >{getDateText(thread)}</Text>
            <Text color='#a5b2bd' fontSize={'12px'} >{thread.status}</Text>
          </Flex>
        </Flex>
        <MessageTextStyled fontSize={'14px'}
        >
          <section>{parser(thread.body || '')}</section>
        </MessageTextStyled>
        {thread._embedded?.attachments && thread._embedded.attachments.length > 0 && (
          <Flex flexWrap={'wrap'} width='100%' maxWidth={'100%'} alignItems='center'>
            <svg width="16" height="16" viewBox="0 0 24 24" style={{ marginRight: '12px' }} fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18.2031 1.9043C17.8391 1.91778 17.4791 1.98561 17.1351 2.10553C15.7514 2.57168 14.5643 3.63784 13.3856 4.81753L5.77017 12.4329C5.09356 13.1095 4.57202 13.9348 4.41417 14.9132C4.33339 15.4464 4.3802 15.9911 4.55078 16.5026C4.72135 17.0142 5.01081 17.478 5.3954 17.856C5.78209 18.2442 6.25668 18.5333 6.77893 18.699C7.30118 18.8647 7.85568 18.902 8.3954 18.8077C9.37202 18.6323 10.1899 18.1089 10.8185 17.4803L19.1834 9.11537C19.2844 9.03413 19.3671 8.93253 19.4262 8.81719C19.4853 8.70185 19.5195 8.57536 19.5265 8.44595C19.5334 8.31654 19.5131 8.1871 19.4668 8.06608C19.4204 7.94505 19.3491 7.83513 19.2574 7.74349C19.1658 7.65185 19.0559 7.58053 18.9349 7.53418C18.8138 7.48783 18.6844 7.46749 18.555 7.47447C18.4256 7.48145 18.2991 7.51561 18.1837 7.57472C18.0684 7.63382 17.9668 7.71656 17.8856 7.81753L9.51971 16.1825C9.12279 16.5794 8.57448 16.9015 8.07787 16.9901C7.58125 17.0788 7.15848 17.0225 6.69325 16.5581C6.20863 16.0735 6.15694 15.6665 6.23171 15.2012C6.30648 14.7369 6.59817 14.2006 7.06802 13.7308L14.6834 6.11537C15.8003 4.99937 16.8683 4.14922 17.712 3.86491C18.5566 3.58153 19.1622 3.64245 20.1351 4.61537C20.8625 5.34184 21.636 5.99722 21.8373 6.66368C21.9388 6.99599 21.9388 7.3283 21.7219 7.87476C21.5059 8.42122 21.0342 9.16891 20.136 10.0671C16.3071 13.8978 13.368 16.8351 11.7702 18.4329C10.3514 19.8517 9.0111 20.7988 7.81848 21.0572C6.62494 21.3166 5.49233 21.0406 4.01079 19.5572C2.5274 18.0738 2.00863 16.6375 2.16463 15.1449C2.31971 13.6514 3.22894 12.0314 4.90525 10.356C6.93233 8.32891 11.1637 4.44276 11.1637 4.44276C11.2664 4.36537 11.3517 4.2674 11.4143 4.15512C11.4769 4.04284 11.5153 3.91872 11.5272 3.79072C11.539 3.66272 11.524 3.53366 11.4831 3.4118C11.4421 3.28994 11.3762 3.17798 11.2895 3.08307C11.2028 2.98817 11.0972 2.91242 10.9796 2.86068C10.8619 2.80894 10.7347 2.78235 10.6062 2.78261C10.4776 2.78287 10.3505 2.80997 10.2331 2.86219C10.1156 2.91441 10.0103 2.99058 9.92402 3.08584C9.92402 3.08584 5.68525 6.92307 3.57787 9.02953C1.71048 10.896 0.534481 12.9028 0.318481 14.9723C0.102481 17.04 0.940635 19.0846 2.71294 20.8569C4.48525 22.6292 6.39787 23.2661 8.19325 22.8757C9.98863 22.4861 11.544 21.2557 13.068 19.7317C14.6659 18.1348 17.6049 15.1966 21.4339 11.3658C22.4493 10.3505 23.1065 9.44307 23.4526 8.56891C23.7988 7.69291 23.8126 6.82984 23.5976 6.1163C23.1665 4.69014 22.0099 3.89353 21.4339 3.31845C20.7729 2.65753 20.0419 2.2403 19.2988 2.04922C18.942 1.95054 18.5732 1.90207 18.2031 1.90522V1.9043Z" fill="#a5b2bd" />
            </svg>
            {thread._embedded.attachments.map(x => {
              return (<AttachmentFileFromHS file={x} />)
            })}
          </Flex>
        )}
      </Flex>
      <Modal
        isOpen={!!modalContent}
        onBackgroundClick={() => setModalContent(null)}
        onEscapeKeydown={() => setModalContent(null)}>
        <Flex flexDirection={'column'}

          style={{
            margin: '0 auto',
            alignSelf: 'center',
            borderRadius: '20px',
            position: 'relative',
            backgroundColor: 'white',
            padding: '24px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
            maxHeight: 'calc(100vh * 0.8)',
            overflow: 'scroll'
          }}>{modalContent}
          <Box onClick={() => setModalContent(null)} style={{ cursor: 'pointer', position: 'absolute', top: '-10px', right: '-10px', height: '34px', width: '34px', display: 'flex', justifyContent: 'center', alignItems: 'center', background: '#F8A294', padding: '6px', borderRadius: '50%', color: 'white' }}>
            X
          </Box>
        </Flex>
      </Modal>
    </Flex>
  )
}

export const Conversations = ({ conversation, session, onEditDraft }: { conversation: Array<IThreadOutput> | null, session: IUser, onEditDraft: (thread: IThread) => void }) => {
  return (
    <>
      {conversation?.map(x => {
        return (<React.Fragment key={x.id}>
          {x._embedded.threads.map(uu => uu)
            // .filter(x => (x.state === 'published' || x.state === 'draft' || x.draft || x.type === 'lineitem')
            .filter(x => (x.state === 'published' || x.state === 'draft' || x.draft || x.type === 'lineitem')
              && x.body !== 'initiate_1'
              && (x.type !== 'note' || (!x.body) || !x.body.startsWith('Set conversation status to'))).map((thread, i, arr) => {
                if (thread.type !== 'lineitem')
                  return (<Thread onEditDraft={onEditDraft} session={session} thread={thread} key={thread.id} />)
                if (thread.type === 'lineitem') {
                  return (<LineItem thread={thread} key={thread.id} />)
                }
              })}
        </React.Fragment>)
      })
      }
    </>
  )
}

const convertToFriendly = (time?: string) => {
  if (!time) return ''
  const mom = moment(time)
  const now = moment()

  if (mom.isSame(now, 'day')) {
    return `Today, ${mom.format('hh:mmA')}`
  }
  if (mom.isSame(moment().add(1, 'day'), 'day')) {
    return `Tomorrow, ${mom.format('hh:mmA')}`
  }
  if (mom.isSame(now, 'week')) {
    return `${mom.format('dddd')}, ${mom.format('hh:mmA')}`
  }
  return `${mom.format('MM/DD/YYYY, hh:mmA')}`
}

const CommunicatorMessager: React.FC<ICommunicatorMessagerProps> = ({ conversationId, session, onSelectConversation, athenaId }) => {
  const [showListConversations, setShowListConversations] = useState(false)
  const [conversation, setConversation] = useState<Array<IThreadOutput> | null>(null);
  const [convoId, setConvoId] = useState<string | null>(null);
  const [isReplying, setIsReplying] = useState<boolean>(false);
  const [initialThread, setInitialThread] = useState<IThread | null>(null)
  const [isNoting, setIsNoting] = useState<boolean>(false);
  const [isTagging, setIsTagging] = useState<boolean>(false);
  const toastId = useRef<any>(null);
  const toastIdUnSnooze = useRef<any>(null);
  const [unsnooze, { loading: loadingUnSnooze, error: errorUnSnooze, data: dataUnSnooze }] = useMutation<IUnsnoozeConversationOutput, IUnsnoozeConversationInput>(UNSNOOZE_CONVERSATION, {
    refetchQueries: [{
      query: GET_CONVERSATION_BY_ID,
      variables: { conversationId: conversationId.toString() }
    }]
  })

  const { data: dataPatientConvos, loading: loadingPatientConvos } = useQuery<IHealthCoachGetPatientConversationsListOutput, IHealthCoachGetPatientConversationsListInput>(GET_PATIENT_CONVERSATIONS_LIST, {
    variables: {
      patientId: athenaId || ''
    }
  })

  useEffect(() => {
    if (!isReplying) {
      setInitialThread(null)
    }
  }, [isReplying])

  let navigate = useNavigate();
  const [loadPatient, { data: dataPatient, loading: loadingPatient }] = useLazyQuery<IGetProviderPatientByIdOutput, IGetProviderPatientByIdInput>(GET_PATIENT_BY_ID, {
    onCompleted: e => {
      console.log({ e })
    }
  })
  const [saveReply, { data: dataSaveReply, loading: loadingSaveReply }] = useMutation<IReplyToConversationOutput, IReplyToConversationInput>(REPLY_TO_CONVERSATION, {
    refetchQueries: [{
      query: GET_CONVERSATIONS, variables: {
        status: 'closed'
      }
    }, {
      query: GET_CONVERSATIONS, variables: {
        status: 'open'
      }
    }, {
      query: GET_CONVERSATION_BY_ID,
      variables: {
        conversationId: conversationId.toString()
      }
    }],
    awaitRefetchQueries: true,
    onError: (e) => {
      if (e && toastId && toastId.current) {
        toast.update(toastId.current, {
          render: (<Flex flexDirection={'column'}>
            <Text fontWeight={400}>Failed to send the message</Text>
            <Text fontWeight={300}>On conversation {conversationId}</Text>
          </Flex>),
          type: toast.TYPE.ERROR,
          isLoading: false,
          autoClose: 5000,
          closeButton: null // The closeButton defined on ToastContainer will be used
        });
      }
    },
    onCompleted: (e) => {
      if (!!e && !!e.healthCoachReplyToConversation && !!e.healthCoachReplyToConversation.redirectTo) {
        navigate(`/messages/${e.healthCoachReplyToConversation.redirectTo}`)
      }
    }
  })
  const refetchQueryOnAddNotes: Array<{
    query: DocumentNode,
    variables: any
  }> = [{
    query: GET_CONVERSATIONS, variables: {
      status: 'closed'
    }
  }, {
    query: GET_CONVERSATIONS, variables: {
      status: 'open'
    }
  }, {
    query: GET_CONVERSATION_BY_ID,
    variables: {
      conversationId: conversationId.toString()
    }
  }]
  if (conversation && conversation.length > 0 && conversation[0].athenaId) {
    refetchQueryOnAddNotes.push({
      query: GET_PATIENT_CASES,
      variables: {
        athenaId: conversation[0].athenaId
      }
    })
  }
  const [addNote, { data: dataAddNote, loading: loadingAddNote }] = useMutation<IAddNoteToConversationOutput, IAddNoteToConversationInput>(ADD_NOTE_TO_CONVERSATION, {
    refetchQueries: refetchQueryOnAddNotes,
    awaitRefetchQueries: true,
    onError: (e) => {
      if (e && toastId && toastId.current) {
        toast.update(toastId.current, {
          render: (<Flex flexDirection={'column'}>
            <Text fontWeight={400}>Failed to send the note</Text>
            <Text fontWeight={300}>On conversation {conversationId}</Text>
          </Flex>),
          type: toast.TYPE.ERROR,
          isLoading: false,
          autoClose: 5000,
          closeButton: null // The closeButton defined on ToastContainer will be used
        });
      }
    }
  })
  const { data, loading, error, refetch } = useQuery<IGetConversationByIdOutput, IIGetConversationByIdInput>(GET_CONVERSATION_BY_ID, {
    variables: { conversationId: conversationId.toString() }
  })

  useEffect(() => {
    if (conversationId !== convoId) {
      setConvoId(conversationId)
      if (convoId !== null)
        setConversation(null)
    }
  }, [conversationId])

  useEffect(() => {
    if (loading) return
    if (!data || !data.providerGetConversationById || !data.providerGetConversationById.page) return
    setConversation([data.providerGetConversationById])
    setConvoId(conversationId)
    if (!!data.providerGetConversationById.athenaId) {
      loadPatient({ variables: { id: data.providerGetConversationById.athenaId } })
    }
  }, [data, loading])

  const handleKeyPress = useCallback((event: any) => {
    if (isNoting || isReplying) return

    if (!!event.key && event.key === 'n') {
      setIsNoting(true)
    }
  }, []);

  useEffect(() => {
    return () => {
      if (toastId && toastId.current) {
        toast.dismiss(toastId.current)
      }
    }
  }, [])

  const toggleReplying = () => {
    if (!!isNoting) return
    setIsReplying(!isReplying)
  }
  const openReplying = () => {
    if (!!isNoting) return
    setIsReplying(true)
  }
  const toggleNoting = () => {
    if (!!isReplying) return
    setIsNoting(!isNoting)
  }
  const openNoting = () => {
    if (!!isReplying) return
    setIsNoting(!isNoting)
  }
  const onDeleteDraft = () => {
    setIsNoting(false)
    setIsReplying(false)
  }
  const onSendReplyHandler = async (html: string, assignedTo: number, status: string, attachments: Array<{
    fileName: string,
    mimeType: string,
    data: string,
    size: number,
  }> | null) => {
    const primaryCustomer = data?.providerGetConversationById.primaryCustomer.id
    if (!primaryCustomer) return
    toastId.current = toast(<Flex flexDirection={'column'}>
      <Text fontWeight={400}>Sending {!!isNoting ? 'note' : 'message'}</Text>
      <Text fontWeight={300}>On conversation {conversationId}</Text>
    </Flex>, { autoClose: false, isLoading: true, closeButton: false });

    let sanitizedAttachments = []
    for (let index = 0; index < (attachments || []).length; index++) {
      const element = (attachments || [])[index];
      sanitizedAttachments.push({
        fileName: element.fileName,
        data: element.data,
        mimeType: element.mimeType
      })
    }
    if (!!isNoting) {
      const dta = {
        text: html,
        assignedTo,
        conversationId: conversationId.toString(),
        status,
        userId: session.helpscoutId,
        attachments: sanitizedAttachments
      }

      await addNote({
        variables: {
          ...dta
        }
      })
      // refetch()
      toggleNoting()
      setInitialThread(null)
      toast.update(toastId.current, {
        render: 'Note Sent!',
        type: toast.TYPE.SUCCESS,
        isLoading: false,
        autoClose: 5000,
        closeButton: null // The closeButton defined on ToastContainer will be used
      });
    } else if (isReplying) {
      const dta = {
        text: html,
        assignedTo,
        conversationId: conversationId.toString(),
        customerId: primaryCustomer,
        status,
        userId: session.helpscoutId,
        attachments: sanitizedAttachments,
        // isDraft: true
      }

      await saveReply({
        variables: {
          ...dta
        }
      })
      toast.update(toastId.current, {
        render: 'Reply Sent!',
        type: toast.TYPE.SUCCESS,
        isLoading: false,
        autoClose: 5000,
        closeButton: null // The closeButton defined on ToastContainer will be used
      });
      // refetch()
      toggleReplying()
      setInitialThread(null)
    }
  }

  if (!!loading) {
    return (<Flex flex={1} marginTop={4} width='100%' height='100%' flexDirection={'column'} justifyContent='center' alignItems={'center'} >
      <LoadingMessages style={{ maxWidth: '50%' }} />
    </Flex>)
  }
  return (
    <Flex flex={1} flexDirection={'column'} style={{ height: '100%', maxHeight: '100%', overflowY: 'auto' }}>
      {!loading && !!data && !!data?.providerGetConversationById && !!data?.providerGetConversationById ? (
        <Flex flexDirection={'column'} width={'100%'}>
          <Flex marginY={2} marginX={3} mr={4} alignItems='center' flexDirection={['column-reverse', 'row', 'row']}>
            <Text fontWeight={500} flex={1} fontSize='20px' mr={3}>{data?.providerGetConversationById.subject}</Text>
            <Text textAlign={'left'} fontWeight={300} fontSize={'12px'} style={{ cursor: 'pointer' }} onClick={() => {

              navigator.clipboard.writeText(`https://secure.helpscout.net/conversation/${data?.providerGetConversationById.id}`);
              toast.success('Helpscout link copied in clipboard', {
                autoClose: 1500
              })
            }}>copy HS link</Text>
            <ConversationStatusCard status={data?.providerGetConversationById.status} />
          </Flex>
          <Flex marginY={2} marginX={3}>
            <Flex flex={1}>
              {data?.providerGetConversationById.tags && (
                <Flex mr={4}>
                  {data?.providerGetConversationById.tags.map(x => {
                    return (
                      <Flex key={x.tag}>
                        <Tag tag={x} />
                      </Flex>
                    )
                  })}
                </Flex>
              )}
            </Flex>
            {!!athenaId && !loadingPatientConvos && dataPatientConvos && dataPatientConvos.healthCoachGetPatientConversationsList && dataPatientConvos.healthCoachGetPatientConversationsList.length > 0 && (
              <Button onClick={() => setShowListConversations(true)} variant={'outline'} style={{ width: 'fit-content', fontSize: '12px', padding: '4px 12px', height: 'fit-content' }}>{dataPatientConvos.healthCoachGetPatientConversationsList.length} Conversations ({dataPatientConvos.healthCoachGetPatientConversationsList.filter(x => x.status === 'active').length} active)</Button>
            )}
          </Flex>

          {data &&
            data.providerGetConversationById &&
            data.providerGetConversationById.convoTree &&
            data.providerGetConversationById.convoTree.tree &&
            data.providerGetConversationById.convoTree.tree.length > 0
            && data.providerGetConversationById.convoTree.parent !== conversationId.toString()
            && (
              <Flex style={{ borderBottom: '1px solid #e5e9ec', width: '100%', padding: '12px', paddingLeft: '30px', background: '#db8e00' }}>
                <Text mr={2} style={{ color: 'white' }}>This conversation is no longer editable. For further communications please use</Text>
                <Link style={{ color: 'white' }} to={`/messages/${data.providerGetConversationById.convoTree.parent}`}>this conversation</Link>
              </Flex>
            )}

          {!loading && (!data
            || !data.providerGetConversationById
            || !data.providerGetConversationById.convoTree
            || !data.providerGetConversationById.convoTree.tree
            || data.providerGetConversationById.convoTree.tree.length < 1
            || data.providerGetConversationById.convoTree.parent === conversationId.toString()) && (
              <>
                {/* {!loadingPatient && ( */}
                <ConversationToolbar
                  athenaId={data.providerGetConversationById.athenaId}
                  onNote={openNoting}
                  isNoting={isNoting}
                  onReply={openReplying}
                  isReplying={isReplying} session={session}
                  refetchConversation={refetch}
                  isHealthcoachEnabled={data.providerGetConversationById.misHealthcoachEnabled}
                  conversation={data?.providerGetConversationById}
                  toggleTagging={() => setIsTagging(true)} />
                {/* )} */}
              </>
            )}
          {!!data && !!data.providerGetConversationById && data.providerGetConversationById.msnooze && (
            <Flex style={{ padding: '6px 18px', background: '#f5f5f5', minHeight: '36px', alignItems: 'center', opacity: !loadingUnSnooze && !loading ? 1 : 0.3 }}>
              <Text flex={1} color={'rgb(95, 99, 104)'}>Snoozed until {convertToFriendly(data.providerGetConversationById.msnooze.reactivateOn)}</Text>
              <Text style={{ cursor: 'pointer' }} onClick={async () => {

                if (toastIdUnSnooze && toastIdUnSnooze.current) {
                  toast.dismiss(toastIdUnSnooze.current)
                }
                toastIdUnSnooze.current = toast(`Removing snooze`, { autoClose: false, isLoading: true, closeButton: false });
                await unsnooze({
                  variables: {
                    conversationId: conversationId.toString()
                  }
                })
                toast.update(toastIdUnSnooze.current, {
                  render: 'Snooze Removed',
                  type: toast.TYPE.SUCCESS,
                  isLoading: false,
                  autoClose: 5000,
                  closeButton: null // The closeButton defined on ToastContainer will be used
                });
              }}>Unsnooze</Text>
            </Flex>
          )}
          <Flex width='100%' style={{
            transition: 'all 300ms ease-in',
            position: 'relative'
          }}>
            {(isReplying || isNoting) && (
              <TextEditor
                initialThread={initialThread}
                athenaId={!!conversation && conversation.length > 0 ? conversation[0].athenaId : athenaId}
                mailboxId={data.providerGetConversationById.mailboxId}
                isClinicalTeam={!!data && !!data.providerGetConversationById && !!data.providerGetConversationById.isClinicalTeam}
                isNote={isNoting}
                onDeleteHandler={onDeleteDraft}
                sendReplyHandler={onSendReplyHandler as any} />)}
            {(isReplying || isNoting) && (!!loadingAddNote || !!loadingSaveReply) && (
              <Flex style={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: 0, background: 'rgba(255,255,255,0.75' }} justifyContent='center' alignItems={'center'}>
                <PleaseWait
                  text={`Sending ${isNoting ? 'note' : 'reply'}`} />
              </Flex>
            )}
          </Flex>
          <Flex flex={1} flexDirection='column' minHeight={['200px', 0, 0]} style={{ paddingTop: '23px', paddingBottom: '23px' }}>
            <Conversations conversation={conversation} session={session} onEditDraft={(thread) => {
              setIsReplying(true)
              setInitialThread(thread)
            }} />
          </Flex>
          {!!showListConversations && !!athenaId && (
            <StyledModal
              isOpen={true}
              onBackgroundClick={() => {
                setShowListConversations(false);
              }}
              onEscapeKeydown={() => {
                setShowListConversations(false);
              }}
            >
              <SpecialModalBackground
                style={{ zIndex: 1000 }}
                onClick={() => {
                  setShowListConversations(false);
                }}
              >
                <Box
                  style={{
                    margin: '0 auto',
                    alignSelf: 'center',
                    borderRadius: '20px',
                    position: 'relative',
                    background: 'white',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexDirection: 'column',
                    maxWidth: '95vw',
                    minWidth: 'initial',
                    maxHeight: '95vh',
                    minHeight: '95vh',
                    overflowY: 'hidden',
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                >
                  <Wrapper flex={1} flexDirection={'column'} style={{ overflowY: 'hidden', padding: 0, width: 'min(95vw, 1250px)', margin: 0 }}>
                    <ViewListConversations athenaId={athenaId} onClose={() => setShowListConversations(false)} />
                  </Wrapper>
                </Box>
              </SpecialModalBackground>
            </StyledModal>
          )}
          {!!isTagging && !!data && data.providerGetConversationById && (
            <StyledModal
              isOpen={true}
              onBackgroundClick={() => {
                setIsTagging(false);
              }}
              onEscapeKeydown={() => {
                setIsTagging(false);
              }}
            >
              <SpecialModalBackground
                style={{ zIndex: 1000 }}
                onClick={() => {
                  setIsTagging(false);
                }}
              >
                <Box
                  style={{
                    margin: '0 auto',
                    alignSelf: 'center',
                    borderRadius: '20px',
                    position: 'relative',
                    background: 'linear-gradient(180deg, #BBD4ED 0%, #FDF5F2 100%)',
                    padding: '24px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexDirection: 'column',
                    maxWidth: '95vw',
                    minWidth: 'initial',
                    maxHeight: '95vh',
                    minHeight: '95vh',
                    overflowY: 'hidden',
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                >
                  <Wrapper flex={1} flexDirection={'column'} width={'800px'} style={{ overflowY: 'auto' }}>
                    <ConvoTagging convo={data.providerGetConversationById} close={() => {
                      setIsTagging(false);
                    }} />
                  </Wrapper>
                </Box>
              </SpecialModalBackground>
            </StyledModal>
          )}
        </Flex>
      ) : null}
    </Flex>);
}

export default withSession(CommunicatorMessager)

const StyledModal = Modal.styled`
  z-index: 1000;
`;
const Wrapper = styled(Flex)`
  overflow: auto;
  width: 800px;
  padding-right: 20px;
  margin-right: 20px;
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #6c89b7;
    border-radius: 5px;
  }
  &::-webkit-scrollbar {
    width: 7px;
    border: 1px solid transparent;
  }
`;
